import React, { useCallback, useMemo } from 'react';

import {
  fetchWithToken,
  getRelationshipServiceUrl,
} from '@adsk/acs-sc-web-platform-api';
import { useCurrentProjectId } from '@adsk/alloy-provider';
import A from '@adsk/alloy-react-a';
import {
  NOTIFICATION_STATUSES,
  useNotifications,
} from '@adsk/alloy-react-notification';

import { useTranslation } from '../i18n';
import { useRelationshipsContext } from '../providers/RelationshipsProvider';
import { CommonReferencePickerPlugin } from '../types/ReferencePickerPlugin';
import { Entity, Relationship } from '../types/types';

interface RequestRelationshipsType {
  entities: Entity[];
}

export type RequestRelationshipsBatchType = RequestRelationshipsType[];

export type Add3LORelationshipsPromiseResponseType = {
  fulfilled: Relationship[];
  rejected: Relationship[];
};

type Is3LOSupportedParam = Pick<Entity, 'domain' | 'type'>;

type Add3LORelationshipsReturnType<T extends boolean> = T extends true
  ? Add3LORelationshipsPromiseResponseType
  : Relationship[];

interface Use3LORelationshipsReturnType {
  is3LOSupported: (params: Is3LOSupportedParam) => boolean;
  add3LORelationships: <T extends boolean = false>(
    sourceEntity: Entity,
    entitiesToLink: Entity[],
    options?: { includeFailedRelationships: T },
  ) => Promise<Add3LORelationshipsReturnType<T>>;
  remove3LORelationshipById: (relationshipId: string) => Promise<string[]>;
}

const chunk = (arr: RequestRelationshipsType[], size: number) => {
  const output = [];
  for (let i = 0; i < arr.length; i += size) {
    output.push(arr.slice(i, i + size));
  }
  return output;
};

export const RELATIONSHIP_ADD_PAGE_SIZE = 20;

/**
 * This hook returns helpers and callbacks to create and delete relationships via the relationship service 3LO API.
 * It must be used inside `RelationshipsProvider` component.
 *
 * Returned value:
 * is3LOSupported - callback to check if a reference type supports 3LO reference creation/deletion. Expects two arguments - a reference entity domain and type.
 * add3LORelationships - callback to add new relationships via 3LO API. Expects two arguments - a source entity and an array of entities to link.
 * remove3LORelationshipById - callback to remove relationship via 3LO API. Expects one argument - the relationship ID to remove.
 *
 * @param plugins
 * @returns {{is3LOSupported: boolean, add3LORelationships: Promise<[]>, remove3LORelationshipById: Promise<string[]>}}
 */
export default (
  targetPlugins: CommonReferencePickerPlugin[] = [],
): Use3LORelationshipsReturnType => {
  const { t } = useTranslation();
  const { showNotification } = useNotifications();
  const { removeRelationshipById, addRelationships } =
    useRelationshipsContext();
  const projectId = useCurrentProjectId();
  const relationshipAddUrl = useMemo(
    () =>
      `${getRelationshipServiceUrl()}/v2/containers/${projectId}/relationships`,
    [projectId],
  );
  const relationshipRemoveUrl = useMemo(
    () =>
      `${getRelationshipServiceUrl()}/v2/containers/${projectId}/relationships:delete`,
    [projectId],
  );

  const is3LOSupported = useCallback(
    ({ domain, type }: Pick<Entity, 'domain' | 'type'>) =>
      targetPlugins.some(
        ({ metadata, is3LOSupported }) =>
          metadata.domain === domain &&
          metadata.type === type &&
          is3LOSupported,
      ),
    [targetPlugins],
  );

  const add3LORelationships = useCallback(
    async <T extends boolean = false>(
      sourceEntity: Entity,
      entitiesToLink: Entity[],
      options?: { includeFailedRelationships?: T },
    ): Promise<Add3LORelationshipsReturnType<T>> => {
      if (!entitiesToLink.length)
        return (
          options?.includeFailedRelationships ? {} : []
        ) as Add3LORelationshipsReturnType<T>;

      const requestRelationships = entitiesToLink.map((entity) => ({
        entities: [sourceEntity, entity],
      }));
      const requestRelationshipsBatches = chunk(
        requestRelationships,
        RELATIONSHIP_ADD_PAGE_SIZE,
      );

      const addRelationshipsBatch = async (
        requestRelationshipsBatch: RequestRelationshipsBatchType,
      ) => {
        let batchFetch: Relationship[];
        try {
          batchFetch = await fetchWithToken(relationshipAddUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestRelationshipsBatch),
          });
        } catch (error) {
          return Promise.reject({
            ...(error as Error),
            relationships: requestRelationshipsBatch,
          });
        }
        return batchFetch;
      };

      const relationshipBatches = await Promise.allSettled(
        requestRelationshipsBatches.map(addRelationshipsBatch),
      );

      let newRelationships: Relationship[] = [];
      let failedRelationships: Relationship[] = [];

      relationshipBatches.forEach((response) => {
        const { status } = response;
        if (status === 'fulfilled') {
          newRelationships = [...newRelationships, ...response.value];
        } else {
          failedRelationships = [
            ...failedRelationships,
            ...response.reason.relationships,
          ];

          console.error(
            'ReferencePicker - ReferencePickerModalContainer: Error adding relationships',
            response.reason,
          );
        }
      });

      if (newRelationships.length === entitiesToLink.length) {
        showNotification({
          status: NOTIFICATION_STATUSES.SUCCESS,
          message: t('ADD_REFERENCE_SUCCESS', {
            count: newRelationships.length,
          }),
        });
      } else {
        const errorMessage = (
          <>
            <span>
              {t('ERROR_ADDING_3LO_RELATIONSHIPS_MESSAGE', {
                count: entitiesToLink.length - newRelationships.length,
                total: entitiesToLink.length,
              })}
            </span>
            {'. '}
            {t('IF_PROBLEM_PERSISTS')}
            <A href="https://knowledge.autodesk.com/contact-support">
              {t('CONTACT_SUPPORT')}
            </A>
            .
          </>
        );

        showNotification({
          status: NOTIFICATION_STATUSES.ERROR,
          message: errorMessage,
        });
      }

      addRelationships(newRelationships);

      if (options?.includeFailedRelationships) {
        return {
          fulfilled: newRelationships,
          rejected: failedRelationships,
        } as Add3LORelationshipsReturnType<T>;
      }

      return newRelationships as Add3LORelationshipsReturnType<T>;
    },
    [addRelationships, relationshipAddUrl, t, showNotification],
  );

  const remove3LORelationshipById = useCallback(
    async (relationshipId: string) => {
      let res: string[];

      try {
        res = await fetchWithToken(relationshipRemoveUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify([relationshipId]),
        });
        removeRelationshipById(relationshipId);
      } catch (error) {
        const errorMessage = (
          <>
            <span>{t('ERROR_REMOVING_RELATIONSHIPS_MESSAGE')}</span>
            {'. '}
            {t('IF_PROBLEM_PERSISTS')}
            <A href="https://knowledge.autodesk.com/contact-support">
              {t('CONTACT_SUPPORT')}
            </A>
            .
          </>
        );

        showNotification({
          status: NOTIFICATION_STATUSES.ERROR,
          message: errorMessage,
        });

        return Promise.reject(error);
      }

      return res;
    },
    [t, relationshipRemoveUrl, removeRelationshipById, showNotification],
  );

  return { is3LOSupported, add3LORelationships, remove3LORelationshipById };
};
